/* Kurello — product page interactions
   Gallery (mobile swipe + dots), lightbox, delivery estimate,
   quantity stepper, model navigation, variant switching, sticky add-to-cart. */
(() => {
  const LT_MONTHS = ['sausio', 'vasario', 'kovo', 'balandžio', 'gegužės', 'birželio', 'liepos', 'rugpjūčio', 'rugsėjo', 'spalio', 'lapkričio', 'gruodžio'];

  /* ---------------- Lightbox ---------------- */
  function setupLightbox(dialog) {
    if (dialog.dataset.ready) return;
    dialog.dataset.ready = 'true';
    const track = dialog.querySelector('[data-k-lb-track]');
    const count = track.children.length;
    if (count < 2) dialog.querySelectorAll('[data-k-lb-step]').forEach((b) => (b.hidden = true));

    const index = () => Math.round(track.scrollLeft / track.clientWidth);
    const go = (i) => {
      const target = Math.max(0, Math.min(count - 1, i));
      track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' });
    };

    dialog.addEventListener('click', (e) => {
      if (e.target.closest('[data-k-lb-close]')) return dialog.close();
      const step = e.target.closest('[data-k-lb-step]');
      if (step) go(index() + Number(step.dataset.kLbStep));
    });
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(index() + 1);
      if (e.key === 'ArrowLeft') go(index() - 1);
    });
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
    });
  }

  function openLightbox(dialog, i) {
    setupLightbox(dialog);
    const track = dialog.querySelector('[data-k-lb-track]');
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      track.scrollLeft = i * track.clientWidth;
    });
  }

  /* ---------------- Gallery ---------------- */
  if (!customElements.get('k-gallery')) {
    customElements.define(
      'k-gallery',
      class KGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.k-gallery__track');
          this.dots = Array.from(this.querySelectorAll('.k-gallery__dot'));
          this.counter = this.querySelector('[data-gallery-current]');
          this.lightbox = document.getElementById(this.dataset.lightbox);
          const slides = Array.from(this.track.children);

          if (this.dots.length && 'IntersectionObserver' in window) {
            const io = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.setActive(slides.indexOf(entry.target));
                });
              },
              { root: this.track, threshold: [0.6] }
            );
            slides.forEach((s) => io.observe(s));
          }

          this.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-lightbox-index]');
            if (!trigger || !this.lightbox) return;
            openLightbox(this.lightbox, Number(trigger.dataset.lightboxIndex));
          });
        }

        setActive(i) {
          this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
          if (this.counter) this.counter.textContent = i + 1;
        }
      }
    );
  }

  /* ---------------- Delivery estimate ---------------- */
  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
  function addBusinessDays(date, days) {
    const d = new Date(date);
    while (days > 0) {
      d.setDate(d.getDate() + 1);
      if (!isWeekend(d)) days--;
    }
    return d;
  }
  function formatRange(a, b) {
    if (a.toDateString() === b.toDateString()) return `${LT_MONTHS[a.getMonth()]} ${a.getDate()} d.`;
    if (a.getMonth() === b.getMonth()) return `${LT_MONTHS[a.getMonth()]} ${a.getDate()}–${b.getDate()} d.`;
    return `${LT_MONTHS[a.getMonth()]} ${a.getDate()} d. – ${LT_MONTHS[b.getMonth()]} ${b.getDate()} d.`;
  }
  function renderDelivery(el) {
    const min = Number(el.dataset.min || 1);
    const max = Math.max(min, Number(el.dataset.max || 3));
    const cutoff = Number(el.dataset.cutoff || 14);
    const now = new Date();
    let ship = new Date(now);
    if (isWeekend(ship) || now.getHours() >= cutoff) ship = addBusinessDays(ship, 1);
    const from = min === 0 ? ship : addBusinessDays(ship, min);
    const to = addBusinessDays(ship, max);
    const prefix = el.dataset.prefix ? `${el.dataset.prefix} ` : '';
    el.innerHTML = `${prefix}<strong>${formatRange(from, to)}</strong>`;
  }

  /* ---------------- Bundle helper ---------------- */
  // Adds the add-on through the shared cart controller (k-cart.js) so the drawer re-renders with it
  async function addAddon(addon) {
    if (window.KCart) return window.KCart.add(addon.dataset.variantId);
    const res = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(addon.dataset.variantId), quantity: 1 }] }),
    });
    return res.json();
  }
  /* ---------------- Section wiring ---------------- */
  function initSection(section) {
    if (section.dataset.kReady) return;
    section.dataset.kReady = 'true';

    section.querySelectorAll('[data-k-delivery]').forEach(renderDelivery);

    // Smart sticky columns (desktop): a column taller than the viewport sticks by its bottom edge
    const columns = [section.querySelector('.k-pdp__media'), section.querySelector('.k-pdp__info')].filter(Boolean);
    const balance = () => {
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      columns.forEach((col) => {
        if (!desktop) return col.style.removeProperty('--k-stick');
        const gap = 24;
        col.style.setProperty('--k-stick', `${Math.min(gap, window.innerHeight - col.offsetHeight - gap)}px`);
      });
    };
    balance();
    window.addEventListener('resize', balance, { passive: true });
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(balance);
      columns.forEach((col) => ro.observe(col));
    }

    // Model selector → navigate to sibling product
    section.querySelectorAll('[data-k-navigate]').forEach((select) => {
      select.addEventListener('change', () => {
        if (select.value) window.location.href = select.value;
      });
    });

    // Quantity stepper
    section.querySelectorAll('[data-k-qty]').forEach((wrap) => {
      const input = wrap.querySelector('input');
      wrap.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-k-qty-step]');
        if (!btn) return;
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.kQtyStep));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      input.addEventListener('blur', () => {
        if (!(parseInt(input.value, 10) >= 1)) input.value = 1;
      });
    });

    // Variant switching (only for products that actually have options)
    const variantsEl = section.querySelector('[data-k-variants]');
    if (variantsEl) {
      const variants = JSON.parse(variantsEl.textContent);
      const selects = Array.from(section.querySelectorAll('[data-k-option]'));
      const idInput = section.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
      selects.forEach((sel) =>
        sel.addEventListener('change', async () => {
          const chosen = selects.map((s) => s.value);
          const variant = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
          const submit = section.querySelector('[data-k-submit]');
          const label = submit && submit.querySelector('.k-buy__label');
          if (!variant) {
            if (submit) submit.disabled = true;
            if (label) label.textContent = window.variantStrings ? window.variantStrings.unavailable : 'Nepasiekiama';
            return;
          }
          if (idInput) idInput.value = variant.id;
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
          if (submit) submit.disabled = !variant.available;
          if (label) label.textContent = variant.available ? submit.dataset.label || label.textContent : window.variantStrings.soldOut;

          // Refresh the price markup from the server so money formatting stays identical
          try {
            const res = await fetch(`${window.location.pathname}?variant=${variant.id}&section_id=${section.dataset.section}`);
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const fresh = doc.querySelector('[data-k-price]');
            const current = section.querySelector('[data-k-price]');
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          } catch (err) {
            console.error(err);
          }
        })
      );
    }

    // Bundle add-on: when ticked, add the wallet right after the case
    const addon = section.querySelector('[data-k-addon]');
    const mainIdInput = section.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
    // PUB_SUB_EVENTS is a top-level const in constants.js, so it is not a window property — use typeof
    if (addon && mainIdInput && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const check = addon.querySelector('[data-k-addon-check]');
      subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        if (!event || event.source !== 'product-form') return;
        if (String(event.productVariantId) !== String(mainIdInput.value)) return;
        // Unticked: the cart drawer itself offers the add-on, nothing to do here
        if (check && check.checked) {
          check.checked = false;
          await addAddon(addon);
        }
      });
    }

    // Sticky add-to-cart
    const sticky = section.querySelector('[data-k-sticky]');
    const buy = section.querySelector('[data-k-buy]');
    if (sticky && buy && 'IntersectionObserver' in window) {
      const stickyBtn = sticky.querySelector('[data-k-sticky-submit]');
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
      });
      io.observe(buy);

      if (stickyBtn) {
        stickyBtn.addEventListener('click', () => {
          const form = document.getElementById(stickyBtn.dataset.kStickySubmit);
          if (!form) return;
          // The sticky header reveals itself when the cart notification opens (see k-header.js)
          if (form.requestSubmit) form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
      }
    }
  }

  const boot = () => document.querySelectorAll('.k-pdp').forEach(initSection);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
